/**
 * TvOperacionPage — tablero de operación para televisor (/tv/operacion).
 *
 * Basado en el dashboard, pero rehecho para una pantalla que nadie toca: el
 * contenido no se desplaza, se REPARTE en dos vistas que se turnan solas.
 *
 *   1. PULSO    la banda de indicadores del período y, debajo, la plantilla
 *               médica, la capacidad del sistema, cómo cerraron las consultas
 *               y lo que exige acción.
 *   2. FLUJO    dónde está la gente ahora (vista de piso) y el TRIAGE por hora:
 *               con qué urgencia clasificó y en qué terminó cada intento.
 *   (además) ORIGEN de las solicitudes y PISO 3D: el centro de salud en 3D con
 *               un recorrido automático de cámara y sin datos de pacientes.
 *
 * SIN DATOS DE PACIENTES. La pantalla cuelga a la vista de una sala entera, así
 * que todo va agregado: contadores y tiempos, nunca nombres ni motivos.
 *
 * DEFINICIONES COMPARTIDAS. «No shows», «canceladas», la capacidad del sistema
 * y las series de triage se calculan exactamente igual que en el dashboard del
 * portal (AdminDashboardPage / TriageStatsSection): las mismas personas miran
 * las dos pantallas y dos cifras distintas para el mismo nombre destruirían la
 * confianza en ambas.
 *
 * TRIAGE. Se leen los agregados diarios (`triage_stats_daily` /
 * `region_stats_daily`) y sus buckets por hora. A diferencia del dashboard, NO
 * hay respaldo sobre eventos crudos: `triage_eventos` lleva uid de pacientes y
 * el rol visor no debe poder leerlos; los buckets existen desde que la métrica
 * se activó, así que aquí bastan.
 *
 * REFRESCO. Las colecciones llegan por `onSnapshot` — ya son tiempo real. El
 * intervalo configurable existe para recalcular lo que depende de "ahora"
 * (esperas, frescura del latido), no para resondear Firestore.
 */
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { where, orderBy, Timestamp } from 'firebase/firestore';
import { ClipboardList, Hourglass, RadioTower, Video } from 'lucide-react';

import { useCollection, useDocument } from '../../hooks/useFirestore';
import { useMedicosConLatido } from '../../hooks/useMedicosConLatido';
import { useModoTV } from '../../hooks/useModoTV';
import { TvShell } from '../../components/tv/TvShell';
import { PisoTopDown } from '../../components/piso/PisoTopDown';
import { MapaOrigen, type PuntoMapa } from '../../components/tv/MapaOrigen';
import { cargarGeo, zonaDe, contornosPanama } from '../../utils/geoZonas';
import {
  Panel, FranjaKpis, ListaAlertas, MapaCalorHora, DonaResumen, RankingApilado, BandaOcupacion,
  LineasHora,
  type Alerta, type ItemKpi, type FilaCalor, type GajoDona, type TonoKpi,
} from '../../components/tv/piezas';
import type { Consulta, Sala, MedicoCompleto, DisposicionConsulta, EstadoConsulta } from '../../types';
import s from './TvOperacionPage.module.css';

const VISTAS = ['Pulso operativo', 'Flujo y piso', 'Origen de solicitudes', 'Piso 3D'] as const;
/** three.js solo se descarga con esta vista; se monta desde el inicio para estar lista en su turno. */
const PisoTV = lazy(() => import('../../components/piso3d/PisoTV'));

/** Latido más viejo que esto = médico desconectado (igual que el dashboard). */
const LATIDO_VIVO_MS = 120_000;
/** A partir de esta espera, la pantalla lo marca en rojo. */
const ESPERA_CRITICA_MIN = 20;
/** Franja del día que se dibuja: fuera de ella no hay operación. */
const HORA_DESDE = 6;
const HORA_HASTA = 22;

// Mismo criterio que AdminDashboardPage: `estado==='no_show'` casi nunca se
// escribe, el cron real cierra con `finalizada` + disposición.
const DISPOSICIONES_NO_EFECTIVAS = new Set<DisposicionConsulta>([
  'paciente_no_presento', 'fallo_tecnico', 'cancelada_medico', 'incompleta',
]);
const esNoAtendida = (c: { estado: EstadoConsulta; disposicion?: DisposicionConsulta }) =>
  c.estado === 'no_show' || (!!c.disposicion && DISPOSICIONES_NO_EFECTIVAS.has(c.disposicion));

// Estados de médico — mismos strings que usa el dashboard para capacidad.
const estaOcupado = (e?: string) => e === 'en_consulta' || e === 'charting' || e === 'buffer';
const estaEnPausa = (e?: string) => e === 'pausa' || e === 'break_almuerzo';

/**
 * Las diez disposiciones del backend agrupadas en cinco desenlaces.
 *
 * A cinco metros, diez filas de códigos internos no se leen; lo que importa es
 * si la consulta terminó bien, si el paciente no apareció o si falló la técnica.
 */
const DESENLACES: { clave: string; etiqueta: string; color: string; de: DisposicionConsulta[] }[] = [
  { clave: 'atendida', etiqueta: 'Atendida', color: 'var(--tv-bien)', de: ['completada', 'referida'] },
  { clave: 'no_respondio', etiqueta: 'Paciente no respondió', color: 'var(--tv-serio)', de: ['paciente_no_presento', 'paciente_rechazo'] },
  { clave: 'cancelada', etiqueta: 'Cancelada', color: 'var(--tv-neutro)', de: ['cancelada_medico', 'cancelada_supervisor', 'cancelada_paciente'] },
  { clave: 'incompleta', etiqueta: 'Incompleta o sin tiempo', color: 'var(--tv-aviso)', de: ['incompleta', 'tiempo_excedido'] },
  { clave: 'falla', etiqueta: 'Falla técnica', color: 'var(--tv-critico)', de: ['fallo_tecnico'] },
];

// ── Triage: mismas escalas y colores que TriageStatsSection ──
const NIVELES_TRIAGE = [
  { nivel: '1', etiqueta: 'N1 · Emergencia', color: '#dc2626' },
  { nivel: '2', etiqueta: 'N2 · Urgente', color: '#f97316' },
  { nivel: '3', etiqueta: 'N3 · Prioritario', color: '#f59e0b' },
  { nivel: '4', etiqueta: 'N4 · Estándar', color: '#3b82f6' },
  { nivel: '5', etiqueta: 'N5 · No urgente', color: '#22c55e' },
] as const;

const RESULTADOS_TRIAGE = [
  { clave: 'teleconsulta', etiqueta: 'Teleconsulta', color: '#16a34a' },
  { clave: 'noCompletaron', etiqueta: 'No completaron', color: '#64748b' },
  { clave: 'noEntraron', etiqueta: 'No pudieron entrar', color: '#8b5cf6' },
  { clave: 'cambiosRegion', etiqueta: 'Cambios de región', color: '#2563eb' },
] as const;

interface TriageDaily {
  fecha: string;
  /** Hora ('0'..'23') → salida → conteo. */
  porHora?: Record<string, Record<string, number>>;
  /** Hora → nivel ('1'..'5') → conteo (solo salidas clínicas). */
  porHoraNivel?: Record<string, Record<string, number>>;
}
interface RegionDaily { fecha: string; porHora?: Record<string, number> }

interface FunnelEnVivo {
  conectados?: number;
  enTriage?: number;
  enEspera?: number;
  enAtencion?: number;
}

const RANGOS = [
  { clave: 'hoy', etiqueta: 'Hoy', dias: 0 },
  { clave: '7d', etiqueta: 'Últimos 7 días', dias: 6 },
  { clave: '30d', etiqueta: 'Últimos 30 días', dias: 29 },
] as const;

type ClaveRango = (typeof RANGOS)[number]['clave'];

/** Fecha ISO en hora de Panamá, que es la que define el "día" de la operación. */
function isoPanama(desplazamientoDias = 0): string {
  const ahora = new Date(Date.now() + desplazamientoDias * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Panama', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(ahora);
}

/** Hora del día (0-23) en Panamá de un instante dado. */
function horaPanama(ms: number): number {
  return Number(new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Panama', hour: '2-digit', hour12: false,
  }).format(new Date(ms)));
}

const N_HORAS = HORA_HASTA - HORA_DESDE;
const HORAS_LBL = Array.from({ length: N_HORAS }, (_, i) => String(HORA_DESDE + i).padStart(2, '0'));
const enFranja = (h: number) => Number.isFinite(h) && h >= HORA_DESDE && h < HORA_HASTA;

export default function TvOperacionPage() {
  const tv = useModoTV({ vistas: VISTAS, claveConfig: 'tv.operacion.config' });
  const [rango, setRango] = useState<ClaveRango>('hoy');

  const dias = RANGOS.find((r) => r.clave === rango)?.dias ?? 0;

  const restricciones = useMemo(() => [
    where('creadoEn', '>=', Timestamp.fromDate(new Date(`${isoPanama(-dias)}T00:00:00-05:00`))),
    where('creadoEn', '<', Timestamp.fromDate(new Date(`${isoPanama(1)}T00:00:00-05:00`))),
    orderBy('creadoEn', 'desc'),
  ], [dias]);

  const restriccionesFecha = useMemo(() => [
    where('fecha', '>=', isoPanama(-dias)),
    where('fecha', '<=', isoPanama(0)),
  ], [dias]);

  const { data: consultas } = useCollection<Consulta>('consultas', restricciones);
  const { data: salas } = useCollection<Sala>('salas');
  const { data: medicos } = useMedicosConLatido<MedicoCompleto>();
  const { data: funnel } = useDocument<FunnelEnVivo>('metricas', 'funnelEnVivo');
  const { data: triageDocs } = useCollection<TriageDaily>('triage_stats_daily', restriccionesFecha);
  const { data: regionDocs } = useCollection<RegionDaily>('region_stats_daily', restriccionesFecha);

  // `tv.tick` entra como dependencia: es lo que hace que las esperas, el latido
  // y la capacidad se recalculen al ritmo que el equipo configuró.
  const m = useMemo(() => {
    const ahora = Date.now();
    const lista = consultas ?? [];

    const enEspera = lista.filter((c) => c.estado === 'en_espera').length;
    const enAtencion = lista.filter((c) => c.estado === 'en_atencion').length;
    const noShows = lista.filter(esNoAtendida).length;
    const canceladas = lista.filter(
      (c) => c.estado === 'cancelada' || (c.disposicion ?? '').startsWith('cancelada_'),
    ).length;

    // Espera de quienes están AHORA en cola: es la que duele y la que justifica
    // una alerta. La media del período va aparte, sobre las ya iniciadas.
    const esperasVivas = lista
      .filter((c) => c.estado === 'en_espera')
      .map((c) => (ahora - (c.creadoEn?.toMillis?.() ?? ahora)) / 60000);
    const esperaMax = esperasVivas.length ? Math.max(...esperasVivas) : 0;

    const conInicio = lista.filter((c) => c.iniciadaEn && c.creadoEn);
    const esperaMedia = conInicio.length
      ? conInicio.reduce(
        (acc, c) => acc + ((c.iniciadaEn!.toMillis?.() ?? 0) - (c.creadoEn?.toMillis?.() ?? 0)) / 60000, 0,
      ) / conInicio.length
      : 0;

    const duraciones = lista
      .map((c) => c.duracionSegundos)
      .filter((d): d is number => typeof d === 'number' && d > 0);
    const duracionMedia = duraciones.length
      ? duraciones.reduce((a, b) => a + b, 0) / duraciones.length / 60
      : 0;

    // Médicos: "en línea" es latido fresco, no el campo `estado` — este último
    // se queda obsoleto si alguien cierra el navegador sin desconectarse.
    const activos = (medicos ?? []).filter((d) => d.activo !== false && d.role === 'medico');
    const enLinea = activos.filter((d) => ahora - (d.ultimoHeartbeat?.toMillis?.() ?? 0) < LATIDO_VIVO_MS);
    const ocupados = enLinea.filter((d) => estaOcupado(d.estado as string)).length;
    const libres = enLinea.filter(
      (d) => !estaOcupado(d.estado as string) && !estaEnPausa(d.estado as string),
    ).length;

    // ── Capacidad del sistema — misma fórmula que el dashboard (DASH-01):
    //    cupos = médicos en turno × (60/tiempoConsulta) × horizonte, topado por
    //    el límite de la sala, menos los pacientes ya en cola o atención. ──
    const asignadoA = (d: MedicoCompleto, salaId: string) => {
      const sa = d.salasAsignadas ?? [];
      return sa.includes(salaId) || sa.includes('*');
    };
    let cuposAhora = 0;
    let capacidadPotencial = 0;
    /** Capacidad dinámica de ESTE momento (sin restar ocupación): es el cupo
     *  del HORIZONTE que usa el gate de admisión, no puestos simultáneos. */
    let capacidadAhora = 0;
    /**
     * Puestos que se pueden atender AL MISMO TIEMPO: un médico en turno atiende
     * una consulta a la vez.
     *
     * Es el denominador correcto para la ocupación de ahora. `capacidadAhora`
     * NO sirve para eso: mide cuántas consultas caben en las próximas horas
     * (médicos × consultas/hora × horizonte), una magnitud de otra unidad.
     * Dividir gente presente entre cupos-de-horizonte daba porcentajes sin
     * sentido — se llegó a ver 360 %.
     */
    // Se acumulan IDS, no una suma: un médico asignado a varias salas —o con
    // comodín '*'— aparece en el recuento de cada una, y sumarlas inflaría el
    // denominador. Una persona es un puesto, esté en las salas que esté.
    const medicosEnPuesto = new Set<string>();
    for (const sala of (salas ?? []).filter((x) => x.activa)) {
      const tConsulta = Number(sala.tiempoConsultaDefault ?? sala.tiempoConsultaMin ?? sala.tiempoEstimadoMin ?? 15) || 15;
      const cuposPorMedicoHora = tConsulta > 0 ? Math.floor(60 / tConsulta) : 0;
      const horizonte = Number(sala.horizonteCapacidadHoras ?? 3);
      const limite = Number(sala.limitePacientes || 0);
      const factorOver = sala.permiteOverbooking === true ? Number(sala.factorOverbooking || 1) : 1;
      const limiteEfectivo = limite > 0 ? Math.floor(limite * factorOver) : 0;

      const asignadosOnline = enLinea.filter((d) => asignadoA(d, sala.id));
      const deTurno = asignadosOnline.filter((d) => !estaEnPausa(d.estado as string));
      const enTurno = deTurno.length;
      deTurno.forEach((d) => medicosEnPuesto.add(d.id));
      const asignadosTotal = activos.filter((d) => asignadoA(d, sala.id)).length;

      const capDin = enTurno * cuposPorMedicoHora * horizonte;
      const capPot = asignadosTotal * cuposPorMedicoHora * horizonte;
      const capDinEf = limiteEfectivo > 0 ? Math.min(capDin, limiteEfectivo) : capDin;
      const capPotEf = limiteEfectivo > 0 ? Math.min(capPot, limiteEfectivo) : capPot;

      const ocupado = lista.filter(
        (c) => c.salaId === sala.id && (c.estado === 'en_espera' || c.estado === 'en_atencion'),
      ).length;
      cuposAhora += Math.max(0, capDinEf - ocupado);
      capacidadPotencial += capPotEf;
      capacidadAhora += capDinEf;
    }

    // ── Producción por médico — TODA consulta que el médico tomó, sin importar
    //    cómo cerró. A propósito NO reutiliza `esNoAtendida` (esa lista es
    //    parcial — le faltan 'tiempo_excedido' y 'paciente_rechazo' entre
    //    otras— y está deliberadamente sincronizada con el KPI "No shows" del
    //    dashboard del portal; ampliarla aquí correría ese número también).
    //    Este panel usa un catch-all propio: efectivas (completada/referida),
    //    en curso (en_atencion) y TODO LO DEMÁS que haya cerrado con médico
    //    asignado — falla técnica, incompleta, tiempo excedido, rechazo del
    //    paciente, no show, cancelada por el médico, etc. Nombres de MÉDICOS,
    //    nunca de pacientes. ──
    const produccion = new Map<string, { etiqueta: string; efectivas: number; enCurso: number; noAtendidas: number }>();
    for (const c of lista) {
      if (!c.medicoId) continue;
      // 'asignada' (oferta híbrida aún sin aceptar) no cuenta: el médico
      // todavía no tomó a nadie, solo tiene una oferta pendiente.
      if (c.estado !== 'en_atencion' && c.estado !== 'finalizada') continue;
      let fila = produccion.get(c.medicoId);
      if (!fila) {
        fila = { etiqueta: c.medicoNombre || 'Sin nombre', efectivas: 0, enCurso: 0, noAtendidas: 0 };
        produccion.set(c.medicoId, fila);
      }
      if (c.disposicion === 'completada' || c.disposicion === 'referida') fila.efectivas += 1;
      else if (c.estado === 'en_atencion') fila.enCurso += 1;
      else fila.noAtendidas += 1;
    }
    const porMedico = [...produccion.entries()].map(([id, x]) => ({
      clave: id,
      etiqueta: x.etiqueta,
      valores: [x.efectivas, x.enCurso, x.noAtendidas],
    }));

    // Consultas creadas por hora (= entraron a espera) y finalizadas por hora
    // de su CIERRE — alimentan la chispa y el embudo horario de capacidad.
    const totalHora = HORAS_LBL.map(() => 0);
    const finalizadasHora = HORAS_LBL.map(() => 0);
    for (const c of lista) {
      const ms = c.creadoEn?.toMillis?.();
      if (ms) {
        const h = horaPanama(ms);
        if (enFranja(h)) totalHora[h - HORA_DESDE] += 1;
      }
      if (c.estado === 'finalizada') {
        const msFin = c.finalizadaEn?.toMillis?.();
        if (msFin) {
          const h = horaPanama(msFin);
          if (enFranja(h)) finalizadasHora[h - HORA_DESDE] += 1;
        }
      }
    }

    const porDesenlace: GajoDona[] = DESENLACES.map((d) => ({
      clave: d.clave,
      etiqueta: d.etiqueta,
      color: d.color,
      valor: lista.filter((c) => c.disposicion && d.de.includes(c.disposicion)).length,
    })).sort((a, b) => b.valor - a.valor);
    const totalCerradas = porDesenlace.reduce((acc, d) => acc + d.valor, 0);

    // Solo para la alerta de sala saturada.
    const salaSaturada = (salas ?? [])
      .map((sala) => ({
        id: sala.id,
        nombre: sala.nombre,
        espera: lista.filter((c) => c.salaId === sala.id && c.estado === 'en_espera').length,
        medicos: enLinea.filter((d) => asignadoA(d, sala.id)).length,
      }))
      .find((x) => x.espera >= 5 && x.medicos === 0) ?? null;

    return {
      total: lista.length, enEspera, enAtencion, noShows, canceladas,
      esperaMax, esperaMedia, duracionMedia,
      medicosTotal: activos.length, medicosEnLinea: enLinea.length,
      medicosOcupados: ocupados, medicosLibres: libres,
      cuposAhora, capacidadPotencial, capacidadAhora,
      puestosSimultaneos: medicosEnPuesto.size,
      porMedico,
      totalHora, finalizadasHora, porDesenlace, totalCerradas, salaSaturada,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultas, medicos, salas, tv.tick]);

  // ── Triage por hora desde los buckets de los agregados diarios ──
  const triage = useMemo(() => {
    const porNivel: Record<string, number[]> = Object.fromEntries(
      NIVELES_TRIAGE.map((n) => [n.nivel, HORAS_LBL.map(() => 0)]),
    );
    const resultados: Record<string, number[]> = Object.fromEntries(
      RESULTADOS_TRIAGE.map((r) => [r.clave, HORAS_LBL.map(() => 0)]),
    );
    // TODAS las salidas del triage por hora, sin filtrar: la demanda real
    // incluye a quien fue rechazado por cupo, derivado por nivel de urgencia
    // (911/presencial), bloqueado por región o que abandonó.
    const solicitadasHora = HORAS_LBL.map(() => 0);
    for (const t of triageDocs ?? []) {
      for (const [hh, porNiv] of Object.entries(t.porHoraNivel ?? {})) {
        const h = Number(hh);
        if (!enFranja(h)) continue;
        for (const [niv, c] of Object.entries(porNiv)) {
          if (porNivel[niv]) porNivel[niv][h - HORA_DESDE] += c || 0;
        }
      }
      for (const [hh, porRes] of Object.entries(t.porHora ?? {})) {
        const h = Number(hh);
        if (!enFranja(h)) continue;
        const i = h - HORA_DESDE;
        resultados.teleconsulta[i] += porRes.teleconsulta || 0;
        resultados.noCompletaron[i] += porRes.abandono || 0;
        resultados.noEntraron[i] += (porRes.region_no_habilitada || 0) + (porRes.cola_llena || 0);
        solicitadasHora[i] += Object.values(porRes).reduce((a, b) => a + (b || 0), 0);
      }
    }
    for (const r of regionDocs ?? []) {
      for (const [hh, c] of Object.entries(r.porHora ?? {})) {
        const h = Number(hh);
        if (enFranja(h)) resultados.cambiosRegion[h - HORA_DESDE] += c || 0;
      }
    }
    const totalNiveles = Object.values(porNivel).flat().reduce((a, b) => a + b, 0);
    const totalResultados = Object.values(resultados).flat().reduce((a, b) => a + b, 0);
    return { porNivel, resultados, solicitadasHora, totalNiveles, totalResultados };
  }, [triageDocs, regionDocs]);

  const alertas = useMemo<Alerta[]>(() => {
    const out: Alerta[] = [];
    if (m.medicosEnLinea === 0 && m.medicosTotal > 0) {
      out.push({
        id: 'nadie', nivel: 'critico', texto: 'Ningún médico conectado',
        detalle: `${m.medicosTotal} médicos activos, ninguno con sesión abierta`,
      });
    } else if (m.enEspera > 0 && m.medicosLibres === 0) {
      out.push({
        id: 'sin-medicos', nivel: 'critico',
        texto: `${m.enEspera} en cola y ningún médico libre`,
        detalle: `${m.medicosOcupados} en consulta`,
      });
    }
    if (m.enEspera > 0 && m.cuposAhora === 0) {
      out.push({
        id: 'sin-cupo', nivel: 'critico',
        texto: `${m.enEspera} en cola y cero cupos disponibles`,
      });
    }
    if (m.esperaMax >= ESPERA_CRITICA_MIN) {
      out.push({
        id: 'espera-larga', nivel: 'critico',
        texto: `Espera máxima de ${Math.round(m.esperaMax)} min`,
        detalle: 'Hay al menos un paciente por encima del umbral',
      });
    } else if (m.esperaMax >= ESPERA_CRITICA_MIN / 2) {
      out.push({ id: 'espera-media', nivel: 'aviso', texto: `Espera máxima de ${Math.round(m.esperaMax)} min` });
    }
    if (m.salaSaturada) {
      out.push({
        id: `sala-${m.salaSaturada.id}`, nivel: 'aviso',
        texto: `${m.salaSaturada.nombre}: ${m.salaSaturada.espera} esperando sin médico en línea`,
      });
    }
    if (m.noShows > 0 && m.total > 0 && m.noShows / m.total > 0.25) {
      out.push({
        id: 'no-atendidas', nivel: 'aviso',
        texto: `${m.noShows} consultas sin atención efectiva`,
        detalle: `${Math.round((m.noShows / m.total) * 100)} % del total del período`,
      });
    }
    return out;
  }, [m]);

  // ── Origen geográfico de las solicitudes ──
  // Los polígonos se cargan bajo demanda (pesan solo en esta pantalla) y la
  // resolución punto→zona es local: sin API de geocodificación que vigilar ni
  // que se caiga con el tablero encendido todo el día.
  const [geoLista, setGeoLista] = useState(false);
  useEffect(() => { void cargarGeo().then(() => setGeoLista(true)).catch(() => setGeoLista(false)); }, []);

  const origen = useMemo(() => {
    const lista = consultas ?? [];
    const puntos: PuntoMapa[] = [];
    // Las zonas del país y las del exterior se cuentan por separado, no por
    // pulcritud: el ranking recorta por volumen, y una solicitud desde fuera
    // —que es justo la excepción que interesa ver— quedaría siempre por debajo
    // de las trece provincias y nunca se mostraría.
    const porZona = new Map<string, number>();
    const porExterior = new Map<string, number>();
    let conUbicacion = 0;
    let fueraDePanama = 0;
    let sinResolver = 0;

    for (const c of lista) {
      const u = c.pacienteUbicacion;
      if (!u || typeof u.lat !== 'number' || typeof u.lng !== 'number') continue;
      conUbicacion += 1;
      const z = geoLista ? zonaDe(u.lat, u.lng) : null;
      puntos.push({ lat: u.lat, lng: u.lng, zona: z?.etiqueta });
      if (!z) { sinResolver += 1; continue; }
      if (z.enPanama) {
        porZona.set(z.etiqueta, (porZona.get(z.etiqueta) ?? 0) + 1);
      } else {
        porExterior.set(z.pais, (porExterior.get(z.pais) ?? 0) + 1);
        fueraDePanama += 1;
      }
    }

    const ordenar = (m: Map<string, number>) =>
      [...m.entries()].map(([n, v]) => ({ n, v })).sort((a, b) => b.v - a.v);

    const zonas = ordenar(porZona);
    const exterior = ordenar(porExterior);

    /**
     * Filas del ranking: las zonas del país por volumen MÁS todas las del
     * exterior, siempre.
     *
     * El corte se hace aquí y no en el componente porque su recorte es por
     * volumen: una solicitud desde Colombia siempre quedaría por debajo de las
     * trece provincias y no se vería nunca, que es exactamente el caso que
     * interesa vigilar. El rótulo "Fuera de Panamá" evita que se lea como una
     * zona más del país.
     */
    const filasZonas = [
      ...zonas.slice(0, 10).map((z) => ({ clave: z.n, etiqueta: z.n, valores: [z.v] })),
      ...exterior.slice(0, 4).map((p) => ({
        clave: `ext-${p.n}`,
        etiqueta: `Fuera de Panamá · ${p.n}`,
        valores: [p.v],
      })),
    ];

    return {
      puntos,
      zonas,
      exterior,
      filasZonas,
      conUbicacion,
      sinUbicacion: lista.length - conUbicacion,
      fueraDePanama,
      sinResolver,
      contornos: geoLista ? contornosPanama() : [],
    };
  }, [consultas, geoLista]);

  // Mismos colores e iconos que /admin/monitor: las dos pantallas las mira la
  // misma gente y cambiarlos aquí rompería el mapa mental del embudo.
  const etapas = useMemo(() => ([
    { clave: 'conectado', etiqueta: 'Conectados', valor: funnel?.conectados ?? 0, color: '#0ea5e9', icono: <RadioTower size={16} /> },
    { clave: 'triage', etiqueta: 'En triage', valor: funnel?.enTriage ?? 0, color: '#8b5cf6', icono: <ClipboardList size={16} /> },
    { clave: 'espera', etiqueta: 'En espera', valor: funnel?.enEspera ?? m.enEspera, color: '#f59e0b', icono: <Hourglass size={16} /> },
    { clave: 'atencion', etiqueta: 'En atención', valor: funnel?.enAtencion ?? m.enAtencion, color: '#3b82f6', icono: <Video size={16} /> },
  ]), [funnel, m.enEspera, m.enAtencion]);

  const kpis: ItemKpi[] = [
    {
      etiqueta: 'Consultas del período', valor: m.total, destacado: true, chispa: m.totalHora,
      notaChispa: `solicitudes por hora · ${String(HORA_DESDE).padStart(2, '0')}–${HORA_HASTA} h`,
      apoyo: `${m.enAtencion} en atención ahora`,
    },
    {
      etiqueta: 'En espera', valor: m.enEspera,
      tono: m.enEspera === 0 ? 'normal' : m.medicosLibres === 0 ? 'critico' : 'aviso',
      apoyo: m.esperaMax > 0 ? `máx. ${Math.round(m.esperaMax)} min en cola` : 'sin cola',
    },
    {
      etiqueta: 'Espera promedio', valor: m.esperaMedia.toFixed(1), unidad: 'min',
      // Sin consultas no hay nada que celebrar: un 0,0 en verde haría pensar
      // que la operación va bien cuando en realidad no hay datos.
      tono: (m.esperaMedia === 0 ? 'normal'
        : m.esperaMedia >= ESPERA_CRITICA_MIN ? 'critico'
          : m.esperaMedia >= ESPERA_CRITICA_MIN / 2 ? 'aviso' : 'bien') as TonoKpi,
      apoyo: `umbral ${ESPERA_CRITICA_MIN} min`,
    },
    {
      etiqueta: 'Duración promedio', valor: m.duracionMedia.toFixed(0), unidad: 'min',
      apoyo: 'por consulta atendida',
    },
    {
      etiqueta: 'Médicos en línea', valor: m.medicosEnLinea,
      tono: m.medicosEnLinea === 0 && m.medicosTotal > 0 ? 'critico' : 'normal',
      apoyo: `${m.medicosLibres} libres · ${m.medicosOcupados} en consulta`,
    },
    {
      etiqueta: 'No shows', valor: m.noShows,
      tono: m.total > 0 && m.noShows / m.total > 0.25 ? 'aviso' : 'normal',
      apoyo: m.total > 0 ? `${Math.round((m.noShows / m.total) * 100)} % del período` : 'sin consultas',
    },
    {
      etiqueta: 'Canceladas', valor: m.canceladas,
      apoyo: m.total > 0 ? `${Math.round((m.canceladas / m.total) * 100)} % del período` : 'sin consultas',
    },
  ];

  const filasNivel: FilaCalor[] = NIVELES_TRIAGE.map((n) => ({
    clave: n.nivel, etiqueta: n.etiqueta, color: n.color, valores: triage.porNivel[n.nivel],
  }));

  const filasResultado: FilaCalor[] = RESULTADOS_TRIAGE.map((r) => ({
    clave: r.clave, etiqueta: r.etiqueta, color: r.color, valores: triage.resultados[r.clave],
  }));

  const contexto = (
    <select
      className={s.selectorRango}
      value={rango}
      onChange={(e) => setRango(e.target.value as ClaveRango)}
      aria-label="Período"
    >
      {RANGOS.map((r) => <option key={r.clave} value={r.clave}>{r.etiqueta}</option>)}
    </select>
  );

  return (
    <TvShell titulo="Operación de teleconsulta" vistas={VISTAS} tv={tv} contexto={contexto}>
      {tv.vistaActual === 0 && (
        <div className={s.vistaPulso}>
          <FranjaKpis items={kpis} />

          <div className={s.pulsoAbajo}>
            {/* Producción del período por médico, como las comparativas del
                dashboard: efectivas / en curso / no atendidas en barra apilada,
                escalada contra el médico con más volumen. */}
            <Panel titulo="Producción por médico" extra={`${m.medicosEnLinea} en línea de ${m.medicosTotal}`}>
              <RankingApilado
                series={[
                  { clave: 'efectivas', etiqueta: 'Efectivas', color: 'var(--tv-bien)' },
                  { clave: 'curso', etiqueta: 'En curso', color: '#3b82f6' },
                  { clave: 'no', etiqueta: 'No atendidas', color: 'var(--tv-serio)' },
                ]}
                filas={m.porMedico}
                textoVacio="Sin consultas con médico en el período"
              />
            </Panel>

            {/* Dos secciones: arriba la ocupación de AHORA (banda = capacidad
                de este momento, segmentos = quién la usa, rayado = libre);
                abajo el embudo horario del período — demanda total del triage
                (con rechazados por cupo, urgencia, región y abandonos), lo que
                entró a espera y lo que se finalizó. Líneas y no pilas: son
                magnitudes anidadas y la brecha entre curvas ES el dato. */}
            <Panel titulo="Capacidad del sistema" extra={`potencial ${m.capacidadPotencial}`}>
              <div className={s.capacidadCol}>
                <div className={s.capSeccion}>
                  {/* Solo lo que OCUPA un puesto entra en el porcentaje. La
                      espera va aparte: antes se sumaba a la ocupación y contra
                      un denominador de otra unidad —los cupos del horizonte—,
                      así que una fila larga con pocos médicos marcaba 360 %. */}
                  <BandaOcupacion
                    segmentos={[
                      { clave: 'atencion', etiqueta: 'En atención', valor: m.enAtencion, color: '#3b82f6' },
                    ]}
                    capacidad={m.puestosSimultaneos}
                    cola={m.enEspera}
                    etiquetaLibre="Médicos libres"
                    notaPie={`Cupos del horizonte: ${m.capacidadAhora} · potencial ${m.capacidadPotencial}`}
                    textoVacio={m.medicosEnLinea === 0 ? 'Sin médicos en línea' : 'Sin capacidad configurada'}
                  />
                </div>

                <div className={s.capSubtitulo}>Solicitudes del período · por hora</div>
                <LineasHora
                  horas={[...HORAS_LBL]}
                  series={[
                    { clave: 'solicitadas', etiqueta: 'Solicitadas', color: '#0ea5e9', valores: triage.solicitadasHora },
                    { clave: 'entraron', etiqueta: 'Entraron a espera', color: '#f59e0b', valores: m.totalHora },
                    { clave: 'finalizadas', etiqueta: 'Finalizadas', color: 'var(--tv-bien)', valores: m.finalizadasHora },
                  ]}
                />
              </div>
            </Panel>

            {/* Columna derecha partida en dos: la disposición arriba (siempre
                tiene contenido) y las alertas debajo (vacías en operación
                normal, así que no merecen media pantalla propias). */}
            <div className={s.colDerecha}>
              <Panel titulo="Disposición de finalizadas" extra={`${m.totalCerradas} cerradas`}>
                <DonaResumen
                  gajos={m.porDesenlace}
                  etiquetaCentro="cerradas"
                  textoVacio="Sin consultas cerradas en el período"
                />
              </Panel>
              <Panel titulo="Requiere atención" extra={alertas.length ? `${alertas.length}` : undefined}>
                <ListaAlertas alertas={alertas} />
              </Panel>
            </div>
          </div>
        </div>
      )}

      {tv.vistaActual === 1 && (
        <div className={s.vistaFlujo}>
          <Panel titulo="Vista de piso en vivo" extra="ahora mismo" className={s.panelPiso}>
            <PisoTopDown etapas={etapas} className={s.pisoTv} />
          </Panel>

          <div className={s.dosGraficos}>
            {/* Dos mapas gemelos sobre la misma base horaria: el primero dice
                CON QUÉ URGENCIA clasificó el triage cada hora; el segundo, EN
                QUÉ TERMINÓ cada intento. El formato idéntico deja comparar
                columnas entre paneles de un solo vistazo. */}
            <Panel titulo="Urgencia del triage · por hora" extra={`${triage.totalNiveles} clasificados`}>
              <MapaCalorHora horas={[...HORAS_LBL]} filas={filasNivel} />
            </Panel>
            <Panel titulo="Resultado del triage · por hora" extra={`${triage.totalResultados} salidas`}>
              <MapaCalorHora horas={[...HORAS_LBL]} filas={filasResultado} />
            </Panel>
          </div>
        </div>
      )}

      {tv.vistaActual === 2 && (
        <div className={s.vistaOrigen}>
          <Panel
            titulo="Desde dónde solicitan"
            extra={origen.conUbicacion > 0 ? `${origen.conUbicacion} con ubicación` : 'sin ubicaciones'}
            className={s.panelMapa}
          >
            <MapaOrigen
              contornos={origen.contornos}
              puntos={origen.puntos}
              fueraDeCuadro={origen.fueraDePanama}
            />
          </Panel>

          <div className={s.colOrigen}>
            {/* Ya no hay panel de países aparte: prácticamente todas las
                solicitudes vienen de Panamá, así que un panel dedicado mostraba
                una sola barra que decía lo que ya se sabía. Las excepciones se
                suman a este ranking rotuladas "Fuera de Panamá · <país>" — sin
                ese rótulo, "Colombia" se leería como una zona más del país. */}
            <Panel titulo="Principales zonas" extra={`${origen.zonas.length + origen.exterior.length} zonas`}>
              <RankingApilado
                series={[{ clave: 'solicitudes', etiqueta: 'Solicitudes', color: 'var(--tv-acento)' }]}
                filas={origen.filasZonas}
                // El tope se calcula para que las filas ya elegidas quepan
                // TODAS: el ranking recorta por volumen, y si se dejara un
                // número fijo volvería a descartar justo las del exterior, que
                // por ser pocas quedan al final de la lista.
                tope={origen.filasZonas.length}
                // Las del exterior llevan prefijo, y con el ancho por defecto
                // se cortaba justo el nombre del país.
                anchoEtiqueta="15em"
                textoVacio="Sin ubicaciones capturadas en el período"
              />
            </Panel>

            {/* La cobertura es parte del dato: sin ella, el mapa parecería
                mostrar TODAS las solicitudes cuando solo muestra las que
                aceptaron compartir su ubicación. */}
            <Panel titulo="Cobertura de la ubicación" extra={`${m.total} solicitudes`}>
              <DonaResumen
                gajos={[
                  { clave: 'con', etiqueta: 'Compartieron ubicación', valor: origen.conUbicacion, color: 'var(--tv-acento)' },
                  { clave: 'sin', etiqueta: 'Sin permiso o no disponible', valor: origen.sinUbicacion, color: 'var(--tv-neutro)' },
                ]}
                valorCentro={m.total > 0 ? `${Math.round((origen.conUbicacion / m.total) * 100)}%` : '—'}
                etiquetaCentro="cobertura"
                textoVacio="Sin solicitudes en el período"
              />
            </Panel>
          </div>
        </div>
      )}

      {/* Siempre montado (oculto y en pausa fuera de su turno): construir la escena toma segundos. */}
      <Suspense fallback={null}>
        <PisoTV activo={tv.vistaActual === 3} duracionMs={tv.rotacionMs} />
      </Suspense>
    </TvShell>
  );
}
